import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Send, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { services } from "@/data/services";
import { formatINR, openWhatsApp } from "@/lib/nc";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  name: "",
  mobile: "",
  location: "",
  service: "",
  budget: "",
  description: "",
};

export const EstimateForm = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  // Prefill the name from the signed-in Google profile (only if untouched)
  useEffect(() => {
    if (user?.name) {
      setForm((prev) => (prev.name ? prev : { ...prev, name: user.name }));
    }
  }, [user]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    const digits = form.mobile.replace(/\D/g, "");
    if (!digits) next.mobile = "Please enter your mobile number";
    else if (!/^[6-9]\d{9}$/.test(digits))
      next.mobile = "Enter a valid 10-digit mobile number";
    if (!form.service) next.service = "Please select a service";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }

    const budgetText = form.budget ? formatINR(form.budget) : "Not specified";
    const message = [
      "*NC – No Contractor: Estimate Request*",
      "",
      `*Name:* ${form.name.trim()}`,
      `*Mobile:* ${form.mobile.trim()}`,
      `*Property Location:* ${form.location.trim() || "Not specified"}`,
      `*Service Required:* ${form.service}`,
      `*Approximate Budget:* ${budgetText}`,
      `*Description of Work:* ${form.description.trim() || "Not specified"}`,
    ].join("\n");

    openWhatsApp(message);
    toast.success("Opening WhatsApp with your details…", {
      description: "Please press send in WhatsApp to reach NC.",
    });
  };

  return (
    <section id="estimate" className="bg-secondary/50 py-12 sm:py-20">
      <div className="container-nc grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        {/* Left intro */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold">
            Request an estimate
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground text-balance sm:text-4xl">
            Tell us about your project
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
            Fill in the details below. When you submit, WhatsApp opens with your information
            pre-filled — simply press send and our team will respond.
          </p>
          <div className="mt-6 flex items-start gap-2 rounded-lg border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <span>Your details are sent only via WhatsApp &mdash; not stored on this website.</span>
          </div>
        </motion.div>

        {/* Form card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.1 }}
        >
          <Card className="p-6 shadow-elegant sm:p-8">
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Customer name <span className="text-gold">*</span></Label>
                  <Input
                    id="name"
                    data-testid="form-name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile number <span className="text-gold">*</span></Label>
                  <Input
                    id="mobile"
                    data-testid="form-mobile"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    value={form.mobile}
                    onChange={(e) => update("mobile", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                    className={errors.mobile ? "border-destructive" : ""}
                  />
                  {errors.mobile && <p className="text-xs text-destructive">{errors.mobile}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Property location</Label>
                <Input
                  id="location"
                  data-testid="form-location"
                  placeholder="Area, locality or address in Jaipur"
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="service">Service required <span className="text-gold">*</span></Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger
                      id="service"
                      data-testid="form-service"
                      className={errors.service ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.title} value={s.title}>
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other / Not sure">Other / Not sure</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.service && <p className="text-xs text-destructive">{errors.service}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Approximate budget</Label>
                  <Input
                    id="budget"
                    data-testid="form-budget"
                    inputMode="numeric"
                    placeholder="e.g. 500000"
                    value={form.budget}
                    onChange={(e) => update("budget", e.target.value.replace(/[^0-9]/g, ""))}
                  />
                  {form.budget && (
                    <p className="text-xs font-medium text-primary">
                      {formatINR(form.budget)}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description of work</Label>
                <Textarea
                  id="description"
                  data-testid="form-description"
                  rows={4}
                  placeholder="Briefly describe the work you need…"
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                />
              </div>

              <Button type="submit" variant="whatsapp" size="xl" className="h-auto min-h-12 w-full whitespace-normal py-3 leading-tight" data-testid="form-submit">
                <MessageCircle /> Send Estimate Request on WhatsApp
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                <Send className="h-3.5 w-3.5" /> Opens WhatsApp with your details pre-filled
              </p>
              <p className="text-center text-xs leading-relaxed text-muted-foreground">
                By submitting this request, you agree to be contacted by NC regarding your project.
              </p>
            </form>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
